import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, DollarSign, FileText, Calendar, Clock } from 'lucide-react';
import { Card, CardHeader, CardTitle, Badge, Select } from '@/components/ui';
import { useItemStore, useSettingsStore, useAuthStore } from '@/stores';
import { formatRelativeDate, formatCurrency, truncate } from '@perfin/shared';

interface RecentActivityProps {
  ownerFilter?: string | null;
}

interface ActivityItem {
  id: string;
  type: 'task' | 'transaction' | 'note' | 'date';
  title: string;
  subtitle?: string;
  status?: string;
  category?: string;
  timestamp: Date;
  href: string;
}

type FilterType = 'all' | 'task' | 'date' | 'transaction' | 'note';
// IMPORTANT: Keep in sync with types/index.ts (TaskStatus, TransactionStatus, NoteKeyDateStatus)
type StatusFilterType = 'all' | 'new' | 'inProgress' | 'complete' | 'draft' | 'pending' | 'final';

const filterOptions = [
  { value: 'all', label: 'All Items' },
  { value: 'task', label: 'Tasks' },
  { value: 'date', label: 'Key Dates' },
  { value: 'transaction', label: 'Transactions' },
  { value: 'note', label: 'Notes' },
];

// All status options
// IMPORTANT: Keep in sync with types/index.ts (TaskStatus, TransactionStatus, NoteKeyDateStatus)
const allStatusOptions: { value: StatusFilterType; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: 'new', label: 'New' },
  { value: 'inProgress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'final', label: 'Final' },
];

// Status options per type
const taskStatusOptions: StatusFilterType[] = ['all', 'new', 'inProgress', 'complete'];
const transactionStatusOptions: StatusFilterType[] = ['all', 'draft', 'pending', 'final'];
const noteKeyDateStatusOptions: StatusFilterType[] = ['all', 'draft', 'final'];

// Get status options based on selected type
const getStatusOptionsForType = (type: FilterType) => {
  if (type === 'task') {
    return allStatusOptions.filter(o => taskStatusOptions.includes(o.value));
  }
  if (type === 'transaction') {
    return allStatusOptions.filter(o => transactionStatusOptions.includes(o.value));
  }
  if (type === 'note' || type === 'date') {
    return allStatusOptions.filter(o => noteKeyDateStatusOptions.includes(o.value));
  }
  // 'all' - show all statuses
  return allStatusOptions;
};

export function RecentActivity({ ownerFilter }: RecentActivityProps) {
  const { user } = useAuthStore();
  const tasks = useItemStore((state) => state.getTasks());
  const transactions = useItemStore((state) => state.getTransactions());
  const notes = useItemStore((state) => state.getNotes());
  const keyDates = useItemStore((state) => state.getKeyDates());
  const { categories, currentUserId } = useSettingsStore();
  const isAdmin = useSettingsStore((state) => state.currentUserRole === 'admin' || state.currentUserRole === 'superAdmin');

  // Filter items by owner filter
  const filterByOwner = <T extends { owner?: string; createdBy: string }>(items: T[]): T[] => {
    if (ownerFilter) {
      return items.filter((item) => item.owner === ownerFilter);
    }
    // All Owners selected
    if (isAdmin) {
      return items; // Admins see all
    }
    // Standard users see items they own OR created
    return items.filter((item) => item.owner === currentUserId || item.createdBy === user?.uid);
  };

  const filteredTasks = filterByOwner(tasks);
  const filteredTransactions = filterByOwner(transactions);
  const filteredNotes = filterByOwner(notes);
  const filteredKeyDates = filterByOwner(keyDates);
  const [filter, setFilter] = useState<FilterType>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>('all');

  // Build category options from all categories
  const categoryOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [{ value: 'all', label: 'All Categories' }];
    categories.forEach((cat) => {
      options.push({ value: cat.id, label: cat.name });
    });
    return options;
  }, [categories]);

  // Calculate dynamic width for category dropdown based on longest label
  // Max: 288px, min is dynamic based on actual content
  const categoryDropdownWidth = useMemo(() => {
    const maxLabelLength = Math.max(...categoryOptions.map((o) => o.label.length));
    // ~8px per character + 32px padding for dropdown arrow
    const calculatedWidth = maxLabelLength * 8 + 32;
    // Cap at 288px max
    return Math.min(calculatedWidth, 288);
  }, [categoryOptions]);

  // Get available status options based on type filter
  const statusOptions = getStatusOptionsForType(filter);

  // Reset status filter when type changes if current status is not valid
  const handleTypeFilterChange = (newType: FilterType) => {
    setFilter(newType);
    const validStatuses = getStatusOptionsForType(newType);
    if (!validStatuses.find(o => o.value === statusFilter)) {
      setStatusFilter('all');
    }
  };

  const recentItems = useMemo(() => {
    const allItems: ActivityItem[] = [];
    const now = new Date();
    const fortyEightHoursAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);

    // Add tasks (filtered by owner)
    filteredTasks.forEach((task) => {
      const timestamp = task.updatedAt?.toDate() || task.createdAt.toDate();
      if (timestamp >= fortyEightHoursAgo) {
        const isCompleted = task.status === 'complete';
        allItems.push({
          id: task.id,
          type: 'task',
          title: task.name || 'Untitled',
          subtitle: isCompleted ? 'Completed' : task.dueDate ? `Due ${formatRelativeDate(task.dueDate)}` : undefined,
          status: task.status,
          category: task.category,
          timestamp,
          href: `/tasks/${task.id}`,
        });
      }
    });

    // Add transactions (filtered by owner)
    filteredTransactions.forEach((transaction) => {
      const timestamp = transaction.updatedAt?.toDate() || transaction.createdAt.toDate();
      if (timestamp >= fortyEightHoursAgo) {
        allItems.push({
          id: transaction.id,
          type: 'transaction',
          title: transaction.name || 'Untitled',
          subtitle: formatCurrency(transaction.amount, transaction.currency),
          status: transaction.status,
          category: transaction.category,
          timestamp,
          href: `/transactions/${transaction.id}`,
        });
      }
    });

    // Add notes (filtered by owner)
    filteredNotes.forEach((note) => {
      const timestamp = note.updatedAt?.toDate() || note.createdAt.toDate();
      if (timestamp >= fortyEightHoursAgo) {
        allItems.push({
          id: note.id,
          type: 'note',
          title: note.name || 'Untitled',
          subtitle: undefined,
          status: (note as unknown as { status?: string }).status,
          category: note.category,
          timestamp,
          href: `/notes/${note.id}`,
        });
      }
    });

    // Add key dates (filtered by owner)
    filteredKeyDates.forEach((keyDate) => {
      const timestamp = keyDate.updatedAt?.toDate() || keyDate.createdAt.toDate();
      if (timestamp >= fortyEightHoursAgo) {
        allItems.push({
          id: keyDate.id,
          type: 'date',
          title: keyDate.name || 'Untitled',
          subtitle: keyDate.dueDate ? formatRelativeDate(keyDate.dueDate) : undefined,
          status: (keyDate as unknown as { status?: string }).status,
          category: keyDate.category,
          timestamp,
          href: `/dates/${keyDate.id}`,
        });
      }
    });

    // Sort by timestamp (most recent first)
    return allItems.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [filteredTasks, filteredTransactions, filteredNotes, filteredKeyDates]);

  // Apply filters (type, category, and status)
  const filteredItems = useMemo(() => {
    let result = recentItems;

    // Filter by type
    if (filter !== 'all') {
      result = result.filter((item) => item.type === filter);
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      result = result.filter((item) => item.category === categoryFilter);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter((item) => item.status === statusFilter);
    }

    return result;
  }, [recentItems, filter, categoryFilter, statusFilter]);

  const typeIcons = {
    task: CheckSquare,
    transaction: DollarSign,
    note: FileText,
    date: Calendar,
  };

  const typeColors = {
    task: 'bg-blue-100 text-blue-600',
    transaction: 'bg-green-100 text-green-600',
    note: 'bg-yellow-100 text-yellow-600',
    date: 'bg-purple-100 text-purple-600',
  };

  const typeLabels = {
    task: 'Task',
    transaction: 'Transaction',
    note: 'Note',
    date: 'Key Date',
  };

  if (recentItems.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
          <CardTitle>Recent Activity</CardTitle>
          <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
            <div style={{ width: categoryDropdownWidth, minWidth: 120 }}>
              <Select
                options={categoryOptions}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              />
            </div>
            <div className="w-32 min-w-[100px]">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilterType)}
              />
            </div>
            <div className="w-36 min-w-[110px]">
              <Select
                options={filterOptions}
                value={filter}
                onChange={(e) => handleTypeFilterChange(e.target.value as FilterType)}
              />
            </div>
          </div>
        </CardHeader>
        <div className="text-center py-8 text-gray-500">
          <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No recent activity</p>
          <p className="text-sm">No items updated in the last 48 hours</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
        <CardTitle>Recent Activity</CardTitle>
        <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
          <div className="w-48 min-w-[120px]">
            <Select
              options={categoryOptions}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            />
          </div>
          <div className="w-32 min-w-[100px]">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilterType)}
            />
          </div>
          <div className="w-36 min-w-[110px]">
            <Select
              options={filterOptions}
              value={filter}
              onChange={(e) => handleTypeFilterChange(e.target.value as FilterType)}
            />
          </div>
        </div>
      </CardHeader>

      {filteredItems.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No {filter === 'all' ? 'recent' : filterOptions.find(o => o.value === filter)?.label.toLowerCase()} activity</p>
          <p className="text-sm">No matching items updated in the last 48 hours</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {filteredItems.map((item) => {
            const Icon = typeIcons[item.type];
            return (
              <Link
                key={`${item.type}-${item.id}`}
                to={item.href}
                className="flex items-center gap-4 py-3 px-1 hover:bg-gray-50 rounded-lg transition-colors -mx-1"
              >
                <div className={`p-2 rounded-lg ${typeColors[item.type]}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {truncate(item.title, 50)}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge size="sm">{typeLabels[item.type]}</Badge>
                    {item.subtitle && (
                      <span className="text-xs text-gray-500">{item.subtitle}</span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  {formatRelativeDate(item.timestamp)}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}
